import { apiV1, microApi, shareApi } from 'app/services/api';

import {
  configureStore,
  ThunkAction,
  Action,
  combineReducers,
  Middleware,
  isRejectedWithValue,
  MiddlewareAPI,
  AnyAction,
  Reducer
} from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER
} from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage
import auth from 'app/features/auth/slice';
import inspection from 'app/features/inspection/slice';

const persistConfig = {
  key: 'root',
  storage,
  // whitelist: [], // elements that will be persisted
  blacklist: [apiV1.reducerPath, microApi.reducerPath, shareApi.reducerPath] // elements that will not be persisted
};
const reducers = combineReducers({
  [shareApi.reducerPath]: shareApi.reducer,
  [apiV1.reducerPath]: apiV1.reducer,
  [microApi.reducerPath]: microApi.reducer,
  auth,
  inspection
});

const rootReducer: Reducer = (state: RootState, action: AnyAction) => {
  if (action.type === 'SIGN_OUT') {
    // this applies to all keys defined in persistConfig(s)
    storage.removeItem('persist:root');

    state = {} as RootState;
  }
  return reducers(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const queryErrorLogger: Middleware =
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (api: MiddlewareAPI) => (next) => (action) => {
    // RTK Query uses `createAsyncThunk` from redux-toolkit under the hood, so we're able to utilize these matchers!
    if (process.env.NODE_ENV !== 'production' && isRejectedWithValue(action)) {
      console.warn('We got a rejected action!');
      console.log('Async Error', action.payload);
    }
    if (action.payload?.status == 401) {
      // if isAuthorized is true and token expired should logout.

      return next({ type: 'SIGN_OUT' });
    }
    return next(action);
  };

export const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV !== 'production',
  middleware: (getDefaultMiddleware) => {
    if (process.env.NODE_ENV !== 'production') {
      return (
        getDefaultMiddleware({
          serializableCheck: {
            ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
          }
        })
          .concat(shareApi.middleware)
          .concat(apiV1.middleware)
          .concat(microApi.middleware)
          // .concat(logger)
          .concat(queryErrorLogger)
      );
    }
    return getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    })
      .concat(shareApi.middleware)
      .concat(apiV1.middleware)
      .concat(microApi.middleware);
  }
});

export const persistor = persistStore(store);
export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
